import React, { useState, useRef, useEffect, useCallback } from "react";
import { API_BASE } from "../AppConfig";
import { useSelector, useDispatch } from "react-redux";
import { loadJob } from "../redux_store/jobState";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    TablePagination,
    TableSortLabel,
} from "@mui/material";
import {
    updateDesigner,
    resetDesigner,
    SHOW_LOADING_BACKDROP,
    setDesignerLoadingBackdrop,
    changePage,
    DESIGNER_PAGE,
} from "../redux_store/appState";
import { fetchTimeout } from "../components/Common";
import BSL_StatusChip from "../components/BSL_StatusChip";
import { makeStyles } from "@material-ui/core/styles";
import { LinearProgress } from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";

const useStyles = makeStyles((theme) => ({
    root: {
        font: "9pt " + theme.typography.fontFamily,
    },
    noJobsFound: {
        width: "100%",
        height: "100px",
        textAlign: "center",
        paddingTop: "40px",
        fontStyle: "italic",
        color: "grey",
        fontSize: "20px",
    },
}));

export default function JobPricesDashboard({ tableData, loading, businessCode, containerMode, dateRange }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.app.user);
    const tokens = useRef({ 0: null });

    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [jobCount, setJobCount] = useState(-1);
    const [pageSize, setPageSize] = useState(10);
    const [hasMore, setHasMore] = useState(true);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [error, setError] = useState(null);
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [orderBy, setOrderBy] = useState(null);
    const [order, setOrder] = useState(null);
    const [loadingJobClicked, setLoadingJobClicked] = useState(false);

    const isStaff = (user?.Role || 0) >= 90;

    const loadPage = useCallback(
        async (pageIx, abortSignal) => {
            if (!selectedStatus) return;

            const tokenToSend = tokens.current[pageIx] ?? null;
            const qs = new URLSearchParams({
                pageSize,
                continuation: tokenToSend ?? "",
                poa: "",
                targetStatus: selectedStatus,
                sortBy: orderBy,
                sortDir: order,
                dateRange: dateRange,
            });
            const endPoint = API_BASE() + `job/dash/${businessCode}?${qs}`;

            try {
                setIsPageLoading(true);
                setError(null);

                const res = await fetchTimeout(endPoint, {
                    method: "POST",
                    signal: abortSignal,
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Container: containerMode }),
                });

                if (!res.ok) {
                    throw new Error(`Server responded ${res.status}`);
                }

                const data = await res.json();
                if (!Array.isArray(data.items)) {
                    throw new Error("Malformed data from server.");
                }

                setRows(data.items);
                setHasMore(!!data.continuation);
                tokens.current[pageIx + 1] = data.continuation ?? null;
            } catch (err) {
                setError("Failed to load jobs. Please try again.");
                setRows([]);
                setHasMore(false);
            } finally {
                setIsPageLoading(false);
            }
        },
        [pageSize, businessCode, containerMode, selectedStatus, order, orderBy, dateRange]
    );

    useEffect(() => {
        if (!selectedStatus) return;

        const abort = new AbortController();
        loadPage(page, abort.signal);

        return () => {
            abort.abort("DeliberateAbort");
        };
    }, [page, pageSize, selectedStatus, order, orderBy, loadPage]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        }).format(amount);
    };

    const openJob = (jobData) => {
        if (loadingJobClicked) return;

        setLoadingJobClicked(true);

        dispatch(changePage(DESIGNER_PAGE));
        dispatch(setDesignerLoadingBackdrop("Loading #" + jobData.id + " - " + jobData.JobName));
        dispatch(loadJob(businessCode, jobData.id, user, containerMode)).then((result) => {
            if (!result) {
                dispatch(
                    setDesignerLoadingBackdrop(
                        <div onClick={() => openJob(jobData)}>
                            <ErrorIcon style={{ fontSize: 40, color: "#ffa8a8" }} />
                            <br />
                            <Typography variant="overline" gutterBottom style={{ marginTop: "100px" }}>
                                <span>
                                    Error Loading # {jobData.id} - {jobData.JobName} ... try again?
                                </span>
                            </Typography>
                        </div>
                    )
                );
                setLoadingJobClicked(false);
            } else {
                dispatch(updateDesigner(SHOW_LOADING_BACKDROP, false));
                dispatch(resetDesigner());
                setLoadingJobClicked(false);
            }
        });
    };

    function formatUnixTimestamp(timestamp) {
        if (timestamp == null) return "";

        const date = new Date(timestamp);

        if (isNaN(date.getTime())) return "";

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    }

    if (loading) return <div>Dashboard Loading…</div>;
    if (!tableData?.length) return <div className={classes.noJobsFound}>- No jobs found -</div>;

    return (
        <>
            <TableContainer style={{ maxWidth: 1300, margin: "20px auto 0" }} component={Paper}>
                <Table>
                    <TableHead style={{ backgroundColor: "#F4F4F4" }}>
                        <TableRow>
                            <TableCell align="center" style={{ fontWeight: "bold" }}>
                                Status
                            </TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>Jobs Total</TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>Not Quoted</TableCell>
                            <TableCell style={{ fontWeight: "bold" }}>Total $</TableCell>
                            {isStaff && <TableCell style={{ fontWeight: "bold" }}>Material $</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableData.map((row, i) => (
                            <TableRow
                                key={`${row.Status}-${i}`}
                                hover
                                style={{
                                    cursor: "pointer",
                                    backgroundColor: row.Status === selectedStatus ? "#e0f7fa" : undefined,
                                }}
                                onClick={() => {
                                    setSelectedStatus(row.Status);
                                    tokens.current = { 0: null };
                                    setPage(0);
                                    setJobCount(row.JobCount);
                                }}
                            >
                                <TableCell align="center">
                                    <BSL_StatusChip status={row.Status} disabled />
                                </TableCell>
                                <TableCell>{row.JobCount}</TableCell>
                                <TableCell>{row.JobCount - row.WithTotalIncGST}</TableCell>
                                <TableCell>{formatCurrency(row.TotalIncGST)}</TableCell>
                                {isStaff && <TableCell>{formatCurrency(row.MaterialBase)}</TableCell>}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {selectedStatus && (
                <TableContainer component={Paper} style={{ maxWidth: 1300, margin: "20px auto" }}>
                    <Table size="small" style={{ tableLayout: "fixed" }}>
                        <TableHead style={{ backgroundColor: "#F4F4F4" }}>
                            <TableRow>
                                <TableCell
                                    style={{ fontWeight: "bold", paddingLeft: "60px" }}
                                    sortDirection={orderBy === "JobID" ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === "JobID"}
                                        direction={orderBy === "JobID" ? order : "asc"}
                                        onClick={() => {
                                            const isAsc = orderBy === "JobID" && order === "asc";
                                            setOrder(isAsc ? "desc" : "asc");
                                            setOrderBy("JobID");
                                            tokens.current = { 0: null };
                                            setPage(0);
                                        }}
                                        style={{ fontWeight: "bold" }}
                                    >
                                        Job ID
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    style={{ fontWeight: "bold" }}
                                    sortDirection={orderBy === "JobName" ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === "JobName"}
                                        direction={orderBy === "JobName" ? order : "asc"}
                                        onClick={() => {
                                            const isAsc = orderBy === "JobName" && order === "asc";
                                            setOrder(isAsc ? "desc" : "asc");
                                            setOrderBy("JobName");
                                            tokens.current = { 0: null };
                                            setPage(0);
                                        }}
                                        style={{ fontWeight: "bold" }}
                                    >
                                        Job Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    style={{ fontWeight: "bold" }}
                                    sortDirection={orderBy === "TotalCost" ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === "TotalCost"}
                                        direction={orderBy === "TotalCost" ? order : "asc"}
                                        onClick={() => {
                                            const isAsc = orderBy === "TotalCost" && order === "asc";
                                            setOrder(isAsc ? "desc" : "asc");
                                            setOrderBy("TotalCost");
                                            tokens.current = { 0: null };
                                            setPage(0);
                                        }}
                                        style={{ fontWeight: "bold" }}
                                    >
                                        Quoted&nbsp;($)
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    style={{ fontWeight: "bold" }}
                                    sortDirection={orderBy === "MaterialCost" ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === "MaterialCost"}
                                        direction={orderBy === "MaterialCost" ? order : "asc"}
                                        onClick={() => {
                                            const isAsc = orderBy === "MaterialCost" && order === "asc";
                                            setOrder(isAsc ? "desc" : "asc");
                                            setOrderBy("MaterialCost");
                                            tokens.current = { 0: null };
                                            setPage(0);
                                        }}
                                        style={{ fontWeight: "bold" }}
                                    >
                                        Material&nbsp;($)
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    style={{ fontWeight: "bold" }}
                                    sortDirection={orderBy === "LastEdited" ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === "LastEdited"}
                                        direction={orderBy === "LastEdited" ? order : "asc"}
                                        onClick={() => {
                                            const isAsc = orderBy === "LastEdited" && order === "asc";
                                            setOrder(isAsc ? "desc" : "asc");
                                            setOrderBy("LastEdited");
                                            tokens.current = { 0: null };
                                            setPage(0);
                                        }}
                                        style={{ fontWeight: "bold" }}
                                    >
                                        Last Edited
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    style={{ fontWeight: "bold" }}
                                    sortDirection={orderBy === "Created" ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === "Created"}
                                        direction={orderBy === "Created" ? order : "asc"}
                                        onClick={() => {
                                            const isAsc = orderBy === "Created" && order === "asc";
                                            setOrder(isAsc ? "desc" : "asc");
                                            setOrderBy("Created");
                                            tokens.current = { 0: null };
                                            setPage(0);
                                        }}
                                        style={{ fontWeight: "bold" }}
                                    >
                                        Created
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r, index) => (
                                <TableRow
                                    key={`${r.id}-${index}`}
                                    sx={{ cursor: "pointer" }}
                                    hover
                                    onClick={() => openJob(r)}
                                >
                                    <TableCell
                                        style={{
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            paddingLeft: "60px",
                                        }}
                                    >
                                        {r.id}
                                    </TableCell>
                                    <TableCell
                                        style={{
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {r.JobName}
                                    </TableCell>
                                    <TableCell>{formatCurrency(r?.TotalIncGST || 0)}</TableCell>
                                    {isStaff && <TableCell>{formatCurrency(r?.MaterialBase || 0)}</TableCell>}
                                    <TableCell>{formatUnixTimestamp(r?._ts) || "-"}</TableCell>
                                    <TableCell>{formatUnixTimestamp(r?.CreationDate) || "-"}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell
                                    colSpan={isStaff ? 6 : 5}
                                    style={{
                                        padding: 0,
                                        borderBottom: "none",
                                        borderTop: "none",
                                        height: 4,
                                    }}
                                >
                                    <div style={{ height: 4 }}>{isPageLoading ? <LinearProgress /> : null}</div>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                    <TablePagination
                        disabled={isPageLoading}
                        component="div"
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        rowsPerPage={pageSize}
                        page={page}
                        count={jobCount}
                        onPageChange={(_, p) => {
                            if (p > page && !hasMore) return;
                            setPage(p);
                        }}
                        onRowsPerPageChange={(e) => {
                            setPageSize(parseInt(e.target.value, 10));
                            tokens.current = { 0: null };
                            setPage(0);
                        }}
                    />

                    {error && <div style={{ color: "red", textAlign: "center", padding: 8 }}>{error}</div>}
                </TableContainer>
            )}
        </>
    );
}
